import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { format, isBefore, startOfDay, addDays, getDay, isSameDay, parseISO } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

interface ServiceOption { id: string; name: string; price: number; duration: number; }
interface Service { id: string; name: string; duration: number; price: number; options: ServiceOption[]; option_required: boolean; option_selection_mode: string; }
interface UpsellOption { id: string; name: string; price: number; duration: number; }
interface Upsell { id: string; name: string; required: boolean; option_selection_mode: string; options: UpsellOption[]; }
interface OpeningHour { day_of_week: number; is_open: boolean; open_time: string; close_time: string; }
interface BlockedTime { start_date: string; end_date: string | null; start_time: string | null; end_time: string | null; type: string; }
interface ExistingReservation { id: string; date: string; duration: number; }

const jsToDbDay = (jsDay: number) => (jsDay === 0 ? 6 : jsDay - 1);

const CreateReservationPage = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const prefillDateStr = searchParams.get("date");
  const prefillTimeStr = searchParams.get("time");
  const prefillDate = prefillDateStr ? new Date(prefillDateStr + "T00:00:00") : undefined;
  const prefillTime = prefillTimeStr || "";

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [selectedUpsellOptionIds, setSelectedUpsellOptionIds] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(prefillDate);
  const [selectedTime, setSelectedTime] = useState(prefillTime);
  const [status, setStatus] = useState("confirmed");

  // Data
  const [services, setServices] = useState<Service[]>([]);
  const [upsells, setUpsells] = useState<Upsell[]>([]);
  const [openingHours, setOpeningHours] = useState<OpeningHour[]>([]);
  const [blockedTimes, setBlockedTimes] = useState<BlockedTime[]>([]);
  const [existingReservations, setExistingReservations] = useState<ExistingReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const selectedService = services.find((s) => s.id === selectedServiceId);

  const totalDuration = useMemo(() => {
    if (!selectedService) return 0;
    const serviceOptDur = selectedService.options
      .filter((o) => selectedOptionIds.includes(o.id))
      .reduce((sum, o) => sum + o.duration, 0);
    const upsellOptDur = upsells.flatMap((u) => u.options)
      .filter((o) => selectedUpsellOptionIds.includes(o.id))
      .reduce((sum, o) => sum + o.duration, 0);
    return selectedService.duration + serviceOptDur + upsellOptDur;
  }, [selectedService, selectedOptionIds, selectedUpsellOptionIds, upsells]);

  const totalPrice = useMemo(() => {
    if (!selectedService) return 0;
    const serviceOptPrice = selectedService.options
      .filter((o) => selectedOptionIds.includes(o.id))
      .reduce((sum, o) => sum + o.price, 0);
    const upsellOptPrice = upsells.flatMap((u) => u.options)
      .filter((o) => selectedUpsellOptionIds.includes(o.id))
      .reduce((sum, o) => sum + o.price, 0);
    return selectedService.price + serviceOptPrice + upsellOptPrice;
  }, [selectedService, selectedOptionIds, selectedUpsellOptionIds, upsells]);

  useEffect(() => {
    if (!profile?.organisation_id) return;
    const fetchAll = async () => {
      setLoading(true);
      const [servicesRes, hoursRes, blockedRes, reservationsRes] = await Promise.all([
        supabase.from("services").select("id, name, duration, price, option_required, option_selection_mode, service_options(id, name, price, duration)").eq("organisation_id", profile.organisation_id!).eq("active", true).order("sort_order"),
        supabase.from("opening_hours").select("day_of_week, is_open, open_time, close_time").eq("organisation_id", profile.organisation_id!),
        supabase.from("blocked_times").select("start_date, end_date, start_time, end_time, type").eq("organisation_id", profile.organisation_id!).eq("active", true),
        supabase.from("reservations").select("id, date, duration").eq("organisation_id", profile.organisation_id!).in("status", ["pending", "confirmed"]),
      ]);
      setServices((servicesRes.data || []).map((s: any) => ({ ...s, options: s.service_options || [] })));
      setOpeningHours(hoursRes.data || []);
      setBlockedTimes(blockedRes.data || []);
      setExistingReservations(reservationsRes.data || []);
      setLoading(false);
    };
    fetchAll();
  }, [profile?.organisation_id]);

  // Fetch upsells when service changes
  useEffect(() => {
    setUpsells([]);
    setSelectedUpsellOptionIds([]);
    if (!selectedServiceId) return;
    const fetchUpsells = async () => {
      const { data } = await supabase
        .from("service_upsells")
        .select("sort_order, upsells(id, name, required, option_selection_mode, upsell_options(id, name, price, duration))")
        .eq("service_id", selectedServiceId)
        .order("sort_order");
      if (data) {
        const fetched: Upsell[] = (data as any[])
          .map((row) => row.upsells)
          .filter(Boolean)
          .map((u: any) => ({ ...u, options: u.upsell_options || [] }));
        setUpsells(fetched);
      }
    };
    fetchUpsells();
  }, [selectedServiceId]);

  useEffect(() => {
    if (services.length > 0 && !selectedServiceId) setSelectedServiceId(services[0].id);
  }, [services]);

  // Availability
  const closedDays = useMemo(() => openingHours.filter((h) => !h.is_open).map((h) => h.day_of_week), [openingHours]);

  const fullDayBlockedDates = useMemo(() => {
    const dates: Date[] = [];
    const parse = (d: string) => { const [y, m, day] = d.split("-").map(Number); return new Date(y, m - 1, day); };
    blockedTimes.forEach((bt) => {
      if (bt.start_time && bt.end_time) return;
      if (bt.type === "single_day" || !bt.end_date) { dates.push(parse(bt.start_date)); }
      else { let cur = parse(bt.start_date); const end = parse(bt.end_date); while (cur <= end) { dates.push(new Date(cur)); cur = addDays(cur, 1); } }
    });
    return dates;
  }, [blockedTimes]);

  const isSlotBlocked = (date: Date, slotStart: number, slotEnd: number) =>
    blockedTimes.some((bt) => {
      if (!bt.start_time || !bt.end_time) return false;
      const [sy, sm, sd] = bt.start_date.split("-").map(Number);
      const btStart = new Date(sy, sm - 1, sd);
      const [ey, em, ed] = bt.end_date ? bt.end_date.split("-").map(Number) : [sy, sm, sd];
      const btEnd = new Date(ey, em - 1, ed);
      const day = startOfDay(date);
      if (day < btStart || day > btEnd) return false;
      const [bsH, bsM] = bt.start_time!.split(":").map(Number);
      const [beH, beM] = bt.end_time!.split(":").map(Number);
      const bStart = bsH * 60 + bsM, bEnd = beH * 60 + beM;
      if (bStart >= bEnd) return false;
      return slotStart < bEnd && slotEnd > bStart;
    });

  const isSlotBooked = (date: Date, slotStart: number, slotEnd: number) =>
    existingReservations.some((r) => {
      const rDate = parseISO(r.date);
      if (!isSameDay(rDate, date)) return false;
      const rStart = rDate.getHours() * 60 + rDate.getMinutes();
      return slotStart < rStart + r.duration && slotEnd > rStart;
    });

  const getAvailableSlots = (date: Date): string[] => {
    const dur = totalDuration || selectedService?.duration || 30;
    const dayOfWeek = jsToDbDay(getDay(date));
    const dayHours = openingHours.find((h) => h.day_of_week === dayOfWeek && h.is_open);
    if (!dayHours) return [];
    const [openH, openM] = dayHours.open_time.split(":").map(Number);
    const [closeH, closeM] = dayHours.close_time.split(":").map(Number);
    const closeMin = closeH * 60 + closeM;
    const slots: string[] = [];
    let m = openH * 60 + openM;
    while (m + dur <= closeMin) {
      if (!isSlotBlocked(date, m, m + dur) && !isSlotBooked(date, m, m + dur))
        slots.push(`${Math.floor(m / 60).toString().padStart(2, "0")}:${(m % 60).toString().padStart(2, "0")}`);
      m += 30;
    }
    return slots;
  };

  const isDateDisabled = (date: Date) => {
    if (isBefore(date, startOfDay(new Date()))) return true;
    if (closedDays.includes(jsToDbDay(getDay(date)))) return true;
    if (fullDayBlockedDates.some((d) => d.getTime() === startOfDay(date).getTime())) return true;
    return false;
  };

  const timeSlots = useMemo(() => {
    if (!selectedDate || openingHours.length === 0) return [];
    return getAvailableSlots(selectedDate);
  }, [selectedDate, openingHours, blockedTimes, existingReservations, totalDuration, selectedService]);

  useEffect(() => {
    if (selectedDate && timeSlots.length > 0 && !timeSlots.includes(selectedTime))
      setSelectedTime(timeSlots[0]);
  }, [selectedDate, timeSlots]);

  const canSubmit = firstName.trim() && email.trim() && selectedServiceId && selectedDate && selectedTime;

  const handleCreate = async () => {
    if (!canSubmit || !profile?.organisation_id) return;
    setSaving(true);
    try {
      const customerName = `${firstName.trim()} ${lastName.trim()}`.trim();
      let customerId: string;
      const { data: existing } = await supabase.from("customers").select("id").eq("organisation_id", profile.organisation_id).eq("email", email.trim().toLowerCase()).maybeSingle();
      if (existing) {
        customerId = existing.id;
        await supabase.from("customers").update({ name: customerName, phone: phone || null }).eq("id", customerId);
      } else {
        const { data: newCust, error: custErr } = await supabase.from("customers").insert({ organisation_id: profile.organisation_id, name: customerName, email: email.trim().toLowerCase(), phone: phone || null }).select("id").single();
        if (custErr || !newCust) throw new Error(custErr?.message || "Failed to create customer");
        customerId = newCust.id;
      }
      const [h, m] = selectedTime.split(":").map(Number);
      const bookingDate = new Date(selectedDate!);
      bookingDate.setHours(h, m, 0, 0);
      const allSelectedOptionIds = [...selectedOptionIds, ...selectedUpsellOptionIds];
      const { data: reservation, error: resErr } = await supabase.from("reservations").insert({
        organisation_id: profile.organisation_id,
        service_id: selectedServiceId,
        customer_id: customerId,
        date: bookingDate.toISOString(),
        duration: totalDuration || selectedService?.duration || 30,
        price: totalPrice,
        selected_options: allSelectedOptionIds,
        status,
        language: "nl",
      }).select("id, reservation_number").single();
      if (resErr || !reservation) throw new Error(resErr?.message || "Failed to create reservation");

      // Fire-and-forget org notification email
      const { data: orgData } = await supabase.from("organisations").select("name, email, address, city, postal_code, country, logo_light_url").eq("id", profile.organisation_id).single();
      if (orgData?.email) {
        const optionNames = allSelectedOptionIds.length > 0
          ? ((await supabase.from("service_options").select("name").in("id", allSelectedOptionIds)).data?.map((o: any) => o.name) || [])
          : [];
        const orgAddress = [orgData.address, orgData.postal_code && orgData.city ? `${orgData.postal_code} ${orgData.city}` : (orgData.city || orgData.postal_code), orgData.country].filter(Boolean).join("\n") || null;
        supabase.functions.invoke("send-booking-confirmation", {
          body: {
            customer_email: email.trim().toLowerCase(),
            customer_name: customerName,
            organisation_name: orgData.name,
            organisation_email: orgData.email,
            organisation_address: orgAddress,
            organisation_logo_url: orgData.logo_light_url || null,
            service_name: selectedService?.name || "",
            option_names: optionNames,
            date: bookingDate.toISOString(),
            time: selectedTime,
            duration: totalDuration || selectedService?.duration || 30,
            price: totalPrice,
            language: "nl",
            reservation_number: reservation.reservation_number,
          },
        }).catch((e: any) => console.error("Notification email error:", e));
      }

      toast.success(`Reservation ${reservation.reservation_number || ""} created!`);
      navigate(`/dashboard/reservations/${reservation.id}`);
    } catch (err: any) {
      toast.error(err.message || "Failed to create reservation");
    } finally {
      setSaving(false);
    }
  };

  const toggleUpsellOption = (upsell: Upsell, optId: string) => {
    const isSingle = upsell.option_selection_mode === "single";
    const upsellOptIds = upsell.options.map((o) => o.id);
    setSelectedUpsellOptionIds((prev) => {
      if (isSingle) {
        const alreadySelected = prev.includes(optId);
        return alreadySelected
          ? prev.filter((id) => id !== optId)
          : [...prev.filter((id) => !upsellOptIds.includes(id)), optId];
      } else {
        return prev.includes(optId) ? prev.filter((id) => id !== optId) : [...prev, optId];
      }
    });
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <Button variant="ghost" size="sm" asChild className="mb-6 -ml-2 text-muted-foreground">
        <Link to="/dashboard/reservations">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to reservations
        </Link>
      </Button>

      <h1 className="text-xl font-bold text-foreground mb-6">New Reservation</h1>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Customer */}
          <section className="rounded-xl border border-border bg-card p-6 shadow-card">
            <h2 className="text-base font-semibold text-foreground mb-4">Customer</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>First name *</Label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jane" />
              </div>
              <div className="space-y-1.5">
                <Label>Last name</Label>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" />
              </div>
              <div className="space-y-1.5">
                <Label>Email *</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+32 ..." />
              </div>
            </div>
          </section>

          {/* Service */}
          <section className="rounded-xl border border-border bg-card p-6 shadow-card">
            <h2 className="text-base font-semibold text-foreground mb-4">Service</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Service *</Label>
                <Select value={selectedServiceId} onValueChange={(v) => { setSelectedServiceId(v); setSelectedOptionIds([]); setSelectedUpsellOptionIds([]); }}>
                  <SelectTrigger><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent>
                    {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedService && selectedService.options.length > 0 && (
              <div className="mt-4 space-y-1.5">
                <Label>Options</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {selectedService.options.map((opt) => {
                    const checked = selectedOptionIds.includes(opt.id);
                    const isSingle = selectedService.option_selection_mode === "single";
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => {
                          if (isSingle) setSelectedOptionIds(checked ? [] : [opt.id]);
                          else setSelectedOptionIds((prev) => checked ? prev.filter((id) => id !== opt.id) : [...prev, opt.id]);
                        }}
                        className={cn(
                          "rounded-lg border px-3 py-1.5 text-sm font-medium transition-all",
                          checked ? "border-primary bg-primary/10 text-primary" : "border-border text-foreground hover:border-primary/40"
                        )}
                      >
                        {opt.name}
                        {opt.price > 0 && <span className="ml-1 text-xs opacity-70">+€{opt.price.toFixed(2)}</span>}
                        {opt.duration > 0 && <span className="ml-1 text-xs opacity-70">+{opt.duration}min</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedService && (
              <p className="mt-3 text-sm text-muted-foreground">
                Duration: <span className="font-medium text-foreground">{totalDuration || selectedService.duration} min</span>
                {" · "}Price: <span className="font-medium text-foreground">€{totalPrice.toFixed(2)}</span>
              </p>
            )}
          </section>

          {/* Upsells */}
          {upsells.length > 0 && (
            <section className="rounded-xl border border-border bg-card p-6 shadow-card">
              <h2 className="text-base font-semibold text-foreground mb-4">Add-ons</h2>
              <div className="space-y-5">
                {upsells.map((upsell) => (
                  <div key={upsell.id}>
                    <p className="text-sm font-medium text-foreground mb-2">
                      {upsell.name}
                      {upsell.required && <span className="ml-1.5 text-xs text-destructive">Required</span>}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {upsell.options.map((opt) => {
                        const checked = selectedUpsellOptionIds.includes(opt.id);
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => toggleUpsellOption(upsell, opt.id)}
                            className={cn(
                              "rounded-lg border px-3 py-1.5 text-sm font-medium transition-all",
                              checked ? "border-primary bg-primary/10 text-primary" : "border-border text-foreground hover:border-primary/40"
                            )}
                          >
                            {opt.name}
                            {opt.price > 0 && <span className="ml-1 text-xs opacity-70">+€{opt.price.toFixed(2)}</span>}
                            {opt.duration > 0 && <span className="ml-1 text-xs opacity-70">+{opt.duration}min</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Date & Time */}
          <section className="rounded-xl border border-border bg-card p-6 shadow-card">
            <h2 className="text-base font-semibold text-foreground mb-4">Date & Time</h2>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="shrink-0">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => { setSelectedDate(d); setSelectedTime(""); }}
                  disabled={isDateDisabled}
                  weekStartsOn={1}
                  className="rounded-xl border border-border p-3 pointer-events-auto"
                />
              </div>
              {selectedDate && (
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-3">
                    Available slots on {format(selectedDate, "EEEE, MMMM d")}
                  </p>
                  {timeSlots.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No available slots on this day.</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {timeSlots.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setSelectedTime(slot)}
                          className={cn(
                            "rounded-lg border px-3 py-2 text-sm font-medium transition-all",
                            selectedTime === slot
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border text-foreground hover:border-primary/30 hover:bg-primary/5"
                          )}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Actions */}
          <div className="flex items-center justify-between pb-6">
            <Button variant="ghost" asChild>
              <Link to="/dashboard/reservations">Cancel</Link>
            </Button>
            <Button onClick={handleCreate} disabled={!canSubmit || saving} size="lg">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create reservation
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateReservationPage;
